// src/api/features/account/types.ts

/**
 * Account feature types.
 *
 * Focused on the logged-in user's own account ("My Account").
 */

import type {
  UserResult,
  Gender,
  IdType,
  OtpChannel,
} from "@/api/features/users/types";

/* =====================================
 * Core result type — My Account
 * ===================================== */

/**
 * Returned by the /myaccount (or /me) endpoint.
 *
 * Combines:
 * - UserResult (standard user fields)
 * - Extra profile fields: avatarUrl
 * - Extra security/verification fields
 */
export type MyAccountResult = UserResult & {
  /** Avatar URL for the logged-in user. */
  avatarUrl?: string | null;

  /** Email verification timestamp (ISO string). */
  emailVerifiedAt?: string | null;

  /** Phone verification timestamp (ISO string). */
  phoneVerifiedAt?: string | null;

  /** Account locked-until timestamp (ISO string). */
  lockedUntil?: string | null;
};

/* =====================================
 * Request body — Update My Account
 * ===================================== */

export interface UpdateMyAccountBody {
  // Core identity
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;

  // Optional contact
  phone?: string | null;

  // Optional profile
  gender?: Gender | null;

  /**
   * DOB as "YYYY-MM-DD" or null to clear.
   */
  dob?: string | null;

  idType?: IdType | null;
  idNumber?: string | null;

  // Address/location
  addressLine1?: string | null;
  addressLine2?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;

  /**
   * Preferred OTP channel.
   */
  otpChannel?: OtpChannel | null;

  /**
   * Avatar URL.
   */
  avatarUrl?: string | null;
}

/* =====================================
 * React Query Keys (shared)
 * ===================================== */

export const AccountKeys = {
  me: ["account", "me"] as const,
};
